// ProfilePage.jsx — Community Identity & Portfolio
// ─────────────────────────────────────────────────────────────────────────────
// "Kaggle × HuggingFace × Are.na for AI research"
// Anonymity-first: users choose exactly what the world sees
// Rich bragging surface: XP, levels, streaks, badges, public work
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth, authFetch } from '../context/AuthContext';
import { auth as authApi } from '../api/client';

const BASE = import.meta.env.VITE_API_URL || 'https://8zgroi30f1.execute-api.us-east-1.amazonaws.com/dev';

async function api(method, path, body) {
  const token = localStorage.getItem('dd_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, {
    method, headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || `${res.status}`), { data });
  return data;
}
const apiGet   = (p, q) => api('GET',   p + (q ? '?' + new URLSearchParams(q) : ''));
const apiPatch = (p, b) => api('PATCH', p, b);
const apiPost  = (p, b) => api('POST',  p, b);

// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
const T = {
  bg:     '#020810', deep:    '#040c18', surface: '#06111e',
  raised: '#091520', b0:      '#0d2030', b1:      '#122840',
  b2:     '#1a3a58', t1:      '#e8f4ff', t2:      '#a8c8e8',
  t3:     '#6890b0', t4:      '#3a6080', cyan:    '#22d3ee',
  green:  '#4ade80', violet:  '#a78bfa', gold:    '#fbbf24',
  orange: '#fb923c', red:     '#f87171', pink:    '#f472b6',
  blue:   '#60a5fa',
};

const TIER = {
  researcher:{ icon:'R', label:'RESEARCHER', color: T.cyan,  bg:`${T.cyan}18`  },
  free:     { icon:'◯', label:'FREE',     color: T.t3,    bg:`${T.t3}18`    },
  annual:   { icon:'⬡', label:'ANNUAL',   color: T.cyan,  bg:`${T.cyan}18`  },
  lifetime: { icon:'⚡', label:'LIFETIME', color: T.gold,  bg:`${T.gold}18`  },
};

const COVER_STYLES = [
  { id:'gradient_1', label:'Deep Ocean',   css:'linear-gradient(135deg,#020c1a 0%,#041a30 45%,#061828 100%)' },
  { id:'gradient_2', label:'Void Violet',  css:'linear-gradient(135deg,#080118 0%,#160530 45%,#080118 100%)' },
  { id:'gradient_3', label:'Dark Emerald', css:'linear-gradient(135deg,#010e08 0%,#031a0e 45%,#020c06 100%)' },
  { id:'gradient_4', label:'Aurora',       css:'linear-gradient(135deg,#020818 0%,#0c0e30 40%,#180626 100%)' },
  { id:'gradient_5', label:'Gold Seam',    css:'linear-gradient(135deg,#0a0800 0%,#1a1200 50%,#0a0800 100%)' },
  { id:'ink',        label:'Pure Ink',     css:'#020810' },
];

const RESEARCH_TAGS = [
  'Machine Learning','Deep Learning','NLP','Computer Vision','Reinforcement Learning',
  'Generative AI','Time Series','Tabular','Audio Analysis','Graph Neural Networks',
  'Federated Learning','Quantum AI','Agentic AI','LLM Alignment','Robotics',
  'Bioinformatics','Finance AI','Medical AI','Climate AI','Edge AI',
];

const BADGES = [
  { id:'joined',       icon:'🌱', name:'Pioneer',        desc:'Joined the den',              color:T.green  },
  { id:'first_lab',    icon:'🔬', name:'Lab Rat',         desc:'First lab completed',          color:T.cyan   },
  { id:'researcher',   icon:'🔭', name:'Deep Researcher', desc:'50+ experiments',              color:T.violet },
  { id:'published_1',  icon:'📡', name:'Broadcaster',     desc:'First public experiment',      color:T.green  },
  { id:'published_10', icon:'📚', name:'Open Scientist',  desc:'10 public experiments',        color:T.cyan   },
  { id:'liked_50',     icon:'💎', name:'Diamond Work',    desc:'50 likes received',            color:T.violet },
  { id:'streak_7',     icon:'🔥', name:'On Fire',         desc:'7-day streak',                 color:T.orange },
  { id:'streak_30',    icon:'⚡', name:'30-Day Streak',   desc:'30 consecutive days',          color:T.gold   },
  { id:'top_10',       icon:'◉',  name:'Top 10%',         desc:'Top decile globally',          color:T.gold   },
];

const XP_LEVELS = [
  {xp:0,    label:'Observer'},     {xp:100,   label:'Apprentice'},
  {xp:300,  label:'Experimenter'}, {xp:700,   label:'Analyst'},
  {xp:1500, label:'Practitioner'}, {xp:3000,  label:'Researcher'},
  {xp:6000, label:'Senior'},       {xp:12000, label:'Expert'},
  {xp:25000,label:'Principal'},    {xp:50000, label:'Distinguished'},
];

function getLevel(xp) {
  for (let i = XP_LEVELS.length - 1; i >= 0; i--)
    if (xp >= XP_LEVELS[i].xp) return { ...XP_LEVELS[i], num: i + 1 };
  return { ...XP_LEVELS[0], num: 1 };
}
function getNextLevel(xp) {
  return XP_LEVELS.find(l => l.xp > xp) || XP_LEVELS[XP_LEVELS.length - 1];
}
function xpPct(xp) {
  const cur = getLevel(xp), nxt = getNextLevel(xp);
  if (cur.xp === nxt.xp) return 100;
  return Math.round((xp - cur.xp) / (nxt.xp - cur.xp) * 100);
}

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US',{month:'short',year:'numeric'}) : '';
const fmtRelative = d => {
  if (!d) return '';
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  if (s < 604800) return `${Math.floor(s/86400)}d ago`;
  return fmtDate(d);
};
const fmt = n => n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n);

// ─── ATOMS ───────────────────────────────────────────────────────────────────

const PBar = ({pct, color=T.cyan, h=4, glow}) => (
  <div style={{height:h,background:T.b1,borderRadius:h,overflow:'hidden'}}>
    <div style={{height:'100%',width:`${Math.min(100,pct||0)}%`,background:color,borderRadius:h,
      transition:'width .6s cubic-bezier(.22,1,.36,1)',
      boxShadow: glow ? `0 0 10px ${color}70` : 'none'}} />
  </div>
);

const Chip = ({children, color=T.cyan, active, onClick}) => (
  <span onClick={onClick} style={{
    display:'inline-block', fontSize:9, padding:'3px 10px', borderRadius:3,
    background: active ? `${color}28` : `${color}10`,
    border:`1px solid ${active ? color : color+'44'}`,
    color: active ? color : color+'aa',
    fontFamily:'JetBrains Mono,monospace', fontWeight: active?700:500,
    cursor: onClick?'pointer':'default', transition:'all .12s',
    letterSpacing:'.06em', whiteSpace:'nowrap',
  }}>{children}</span>
);

const TierPill = ({tier, size='sm'}) => {
  const cfg = TIER[tier]||TIER.free;
  return (
    <span style={{
      fontSize: size==='lg'?10:8, padding: size==='lg'?'3px 12px':'2px 8px',
      background:cfg.bg, border:`1px solid ${cfg.color}55`,
      borderRadius:2, color:cfg.color, fontFamily:'JetBrains Mono,monospace',
      fontWeight:700, letterSpacing:'.1em',
      boxShadow:`0 0 8px ${cfg.color}25`,
    }}>{cfg.icon} {cfg.label}</span>
  );
};

const Avatar = ({src, name='?', size=72, tier}) => {
  const cfg = TIER[tier]||TIER.free;
  const initials = (name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
  const [err, setErr] = useState(false);
  return (
    <div style={{position:'relative',flexShrink:0,width:size,height:size}}>
      <div style={{
        width:size, height:size, borderRadius:'50%', overflow:'hidden',
        background:T.raised, border:`2.5px solid ${cfg.color}60`,
        boxShadow:`0 0 20px ${cfg.color}30, 0 0 50px ${cfg.color}10`,
        display:'flex', alignItems:'center', justifyContent:'center',
      }}>
        {src && !err
          ? <img src={src} alt={name} style={{width:'100%',height:'100%',objectFit:'cover'}}
              onError={() => setErr(true)} />
          : <span style={{fontFamily:'Syne,sans-serif',fontWeight:900,
              fontSize:size*.36,color:cfg.color,letterSpacing:'-.03em'}}>{initials}</span>
        }
      </div>
      {tier && tier!=='free' && (
        <div style={{
          position:'absolute', bottom:-2, right:-2,
          width:size*.34, height:size*.34, borderRadius:'50%',
          background:T.deep, border:`1.5px solid ${cfg.color}`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:size*.18, boxShadow:`0 0 10px ${cfg.color}60`,
        }}>{cfg.icon}</div>
      )}
    </div>
  );
};

const StatNum = ({value, label, color=T.cyan, sub}) => (
  <div style={{textAlign:'center', padding:'10px 6px'}}>
    <div style={{fontFamily:'Syne,sans-serif',fontWeight:900,fontSize:24,
      color, lineHeight:1, letterSpacing:'-.03em'}}>{fmt(value||0)}</div>
    {sub&&<div style={{fontSize:7,color:T.t4,fontFamily:'JetBrains Mono,monospace',marginTop:1}}>{sub}</div>}
    <div style={{fontSize:8,color:T.t3,fontFamily:'JetBrains Mono,monospace',
      letterSpacing:'.1em',marginTop:3,textTransform:'uppercase'}}>{label}</div>
  </div>
);

// ─── ACTIVITY HEATMAP ────────────────────────────────────────────────────────

function Heatmap({activity={}}) {
  const days = [];
  for (let i=89;i>=0;i--) {
    const d=new Date(); d.setDate(d.getDate()-i);
    const k=d.toISOString().slice(0,10);
    days.push({k, n: activity[k]||0});
  }
  const max = Math.max(...days.map(d=>d.n), 1);
  const weeks = [];
  for (let i=0;i<days.length;i+=7) weeks.push(days.slice(i,i+7));
  return (
    <div>
      <div style={{display:'flex',gap:3,marginBottom:6}}>
        {weeks.map((wk,wi) => (
          <div key={wi} style={{display:'flex',flexDirection:'column',gap:3}}>
            {wk.map((day,di) => {
              const heat = day.n/max;
              return (
                <div key={di} title={`${day.k}: ${day.n} actions`}
                  style={{width:10,height:10,borderRadius:2,cursor:'default',
                    background: day.n===0 ? T.b0 : `rgba(34,211,238,${.15+heat*.75})`,
                    border: day.n>0 ? `1px solid rgba(34,211,238,.2)` : 'none',
                  }}/>
              );
            })}
          </div>
        ))}
      </div>
      <div style={{display:'flex',gap:6,alignItems:'center'}}>
        <span style={{fontSize:7,color:T.t4,fontFamily:'JetBrains Mono,monospace'}}>LESS</span>
        {[0,.25,.5,.75,1].map(v=>(
          <div key={v} style={{width:9,height:9,borderRadius:2,
            background:v===0?T.b0:`rgba(34,211,238,${.15+v*.75})`}}/>
        ))}
        <span style={{fontSize:7,color:T.t4,fontFamily:'JetBrains Mono,monospace'}}>MORE</span>
      </div>
    </div>
  );
}

// ─── BADGE DISPLAY ───────────────────────────────────────────────────────────

function BadgeGrid({earned=[], mini}) {
  return (
    <div style={{display:'grid',gridTemplateColumns:`repeat(${mini?9:4},1fr)`,gap:mini?6:10}}>
      {BADGES.map(b => {
        const has = earned.includes(b.id);
        return (
          <div key={b.id} title={`${b.name}: ${b.desc}`} style={{
            textAlign:'center', padding: mini?'6px 4px':'10px 8px',
            background: has?T.surface:T.deep,
            border:`1px solid ${has?b.color+'55':T.b0}`,
            borderRadius:4, opacity: has?1:.4,
            transition:'all .15s',
          }}>
            <div style={{fontSize: mini?16:22, marginBottom:mini?2:5,
              filter: has?'none':'grayscale(1) opacity(.4)'}}>{b.icon}</div>
            {!mini && <>
              <div style={{fontSize:8,color:has?T.t1:T.t4,fontFamily:'JetBrains Mono,monospace',
                fontWeight:700,marginBottom:2,lineHeight:1.2}}>{b.name}</div>
              <div style={{fontSize:7,color:T.t3,lineHeight:1.4}}>{b.desc}</div>
              {has&&<div style={{fontSize:7,color:b.color,marginTop:4,fontFamily:'JetBrains Mono,monospace'}}>✓ earned</div>}
            </>}
          </div>
        );
      })}
    </div>
  );
}

// ─── WORK CARD ───────────────────────────────────────────────────────────────

function WorkCard({exp, isOwn, onLike, liked, onTogglePublic}) {
  const [liking, setLiking] = useState(false);
  const modColor = {
    'ML':T.green,'NLP':T.violet,'Audio':T.orange,'Image':T.pink,
    'Video':T.gold,'Speech':T.cyan,'Agent':T.red,'Data':T.blue,
  };
  const mc = Object.entries(modColor).find(([k])=>exp.module?.includes(k))?.[1]||T.t3;

  return (
    <div style={{
      background:T.surface, border:`1px solid ${T.b1}`, borderRadius:6,
      overflow:'hidden', cursor:'pointer', transition:'border-color .15s, transform .15s',
    }}
      onMouseEnter={e=>{e.currentTarget.style.borderColor=T.b2;e.currentTarget.style.transform='translateY(-1px)';}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor=T.b1;e.currentTarget.style.transform='none';}}>

      {/* Thumbnail */}
      {exp.thumbnail
        ? <img src={exp.thumbnail} alt="" style={{width:'100%',height:110,objectFit:'cover',display:'block',filter:'brightness(.85)'}}/>
        : <div style={{height:72,background:`linear-gradient(135deg,${mc}12,${T.deep})`,
            borderBottom:`1px solid ${mc}18`,display:'flex',alignItems:'center',
            justifyContent:'center',fontSize:24,opacity:.3}}>⬟</div>
      }

      <div style={{padding:'12px 14px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          {exp.module && <Chip color={mc}>{exp.module}</Chip>}
          <span style={{fontSize:8,color:T.t4,fontFamily:'JetBrains Mono,monospace'}}>{fmtRelative(exp.created_at)}</span>
        </div>
        <div style={{fontFamily:'Syne,sans-serif',fontWeight:700,fontSize:13,color:T.t1,
          marginBottom:exp.description?6:0,lineHeight:1.3}}>{exp.title||'Untitled'}</div>
        {exp.description&&(
          <div style={{fontSize:10,color:T.t3,lineHeight:1.6,marginBottom:8,
            display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>
            {exp.description}
          </div>
        )}
        {exp.tags?.length>0&&(
          <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:8}}>
            {exp.tags.slice(0,3).map(tag=><Chip key={tag} color={T.t3}>{tag}</Chip>)}
          </div>
        )}

        <div style={{display:'flex',gap:8,alignItems:'center',paddingTop:8,borderTop:`1px solid ${T.b0}`}}>
          <button onClick={async e=>{e.stopPropagation();if(liking)return;setLiking(true);try{await onLike?.(exp.id)}finally{setLiking(false);}}}
            style={{display:'flex',alignItems:'center',gap:5,padding:'4px 8px',
              background:liked?`${T.violet}18`:'transparent',
              border:`1px solid ${liked?T.violet:T.b1}`,
              borderRadius:3,color:liked?T.violet:T.t4,fontSize:9,cursor:'pointer',
              fontFamily:'JetBrains Mono,monospace',transition:'all .12s'}}>
            {liked?'♥':'♡'} {exp.likes||0}
          </button>
          <span style={{fontSize:9,color:T.t4,fontFamily:'JetBrains Mono,monospace'}}>◎ {exp.views||0}</span>
          {isOwn&&(
            <button onClick={e=>{e.stopPropagation();onTogglePublic?.(exp);}}
              style={{marginLeft:'auto',padding:'3px 8px',
                background:exp.is_public?`${T.green}12`:`${T.t4}10`,
                border:`1px solid ${exp.is_public?T.green:T.b1}`,
                borderRadius:3,color:exp.is_public?T.green:T.t4,
                fontSize:8,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>
              {exp.is_public?'◉ PUBLIC':'◌ PRIVATE'}
            </button>
          )}
          {!isOwn&&exp.author&&!exp.author.is_anonymous&&(
            <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:5}}>
              <Avatar src={exp.author.avatar_url} name={exp.author.display_name} size={18} tier={exp.author.tier}/>
              <span style={{fontSize:8,color:T.t3,fontFamily:'JetBrains Mono,monospace'}}>{exp.author.display_name||exp.author.username}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── TIMELINE ────────────────────────────────────────────────────────────────

function Timeline({items=[]}) {
  const icons = {lab_complete:'✓',experiment_saved:'💾',badge_earned:'🏆',joined:'🌱',upgrade:'⚡',challenge_solved:'🎯'};
  return (
    <div style={{display:'flex',flexDirection:'column',gap:0}}>
      {items.map((item,i) => (
        <div key={i} style={{display:'flex',gap:14,paddingBottom:16,
          borderLeft:`1px solid ${T.b0}`,paddingLeft:20,position:'relative'}}>
          <div style={{position:'absolute',left:-8,top:2,width:15,height:15,
            background:T.surface,border:`1px solid ${T.b1}`,borderRadius:'50%',
            display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,color:T.t3}}>
            {icons[item.type]||'·'}
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.t1,marginBottom:2,lineHeight:1.4}}>{item.title}</div>
            {item.desc&&<div style={{fontSize:10,color:T.t3,lineHeight:1.5}}>{item.desc}</div>}
            {item.module&&(
              <span style={{display:'inline-block',marginTop:4,fontSize:8,color:T.cyan,
                background:`${T.cyan}12`,padding:'2px 7px',borderRadius:2,fontFamily:'JetBrains Mono,monospace'}}>
                {item.module}
              </span>
            )}
          </div>
          <div style={{fontSize:8,color:T.t4,whiteSpace:'nowrap',paddingTop:2,fontFamily:'JetBrains Mono,monospace'}}>
            {fmtRelative(item.created_at)}
          </div>
        </div>
      ))}
      {!items.length&&(
        <div style={{fontSize:11,color:T.t4,padding:'20px 0',textAlign:'center',fontFamily:'JetBrains Mono,monospace'}}>
          No activity yet.
        </div>
      )}
    </div>
  );
}

// ─── EDIT PROFILE DRAWER ─────────────────────────────────────────────────────

function EditDrawer({user, onClose, onSaved}) {
  const [form, setForm] = useState({
    display_name:  user.display_name||user.full_name||'',
    bio:           user.bio||'',
    location:      user.location||'',
    website:       user.website||'',
    affiliation:   user.affiliation||'',
    pronouns:      user.pronouns||'',
    social_github:   user.social_github||'',
    social_twitter:  user.social_twitter||'',
    social_linkedin: user.social_linkedin||'',
    social_scholar:  user.social_scholar||'',
    research_focus:  user.research_focus||[],
    is_anonymous:    user.is_anonymous||false,
    cover_style:     user.cover_style||'gradient_1',
    avatar_url:      user.avatar_url||'',
  });
  const [tab, setTab]     = useState('identity');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [saved, setSaved]   = useState(false);

  const set = k => v => setForm(f => ({...f, [k]: v}));

  const toggleTag = tag => {
    const cur = form.research_focus;
    set('research_focus')(cur.includes(tag) ? cur.filter(t=>t!==tag) : [...cur, tag].slice(0,12));
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const {user: updated} = await apiPatch('/users/me', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved?.(updated);
    } catch(e) {
      setError(e.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    {id:'identity',label:'Identity'},
    {id:'social',  label:'Social'},
    {id:'focus',   label:'Research Focus'},
    {id:'privacy', label:'Privacy & Cover'},
  ];

  const Input = ({label, field, placeholder, maxLen, type='text', hint}) => (
    <div style={{marginBottom:16}}>
      <label style={{display:'block',fontSize:9,color:T.t3,fontFamily:'JetBrains Mono,monospace',
        letterSpacing:'.1em',marginBottom:6,textTransform:'uppercase'}}>{label}</label>
      {type==='textarea'
        ? <textarea value={form[field]} onChange={e=>set(field)(e.target.value)} placeholder={placeholder}
            maxLength={maxLen} rows={3}
            style={{width:'100%',background:T.deep,border:`1px solid ${T.b2}`,color:T.t1,
              padding:'10px 12px',fontSize:12,borderRadius:4,outline:'none',resize:'vertical',
              fontFamily:'inherit',boxSizing:'border-box',lineHeight:1.6,
              ':focus':{borderColor:T.cyan}}}
            onFocus={e=>e.target.style.borderColor=T.cyan}
            onBlur={e=>e.target.style.borderColor=T.b2}/>
        : <input type={type} value={form[field]} onChange={e=>set(field)(e.target.value)}
            placeholder={placeholder} maxLength={maxLen}
            style={{width:'100%',background:T.deep,border:`1px solid ${T.b2}`,color:T.t1,
              padding:'10px 12px',fontSize:12,borderRadius:4,outline:'none',
              fontFamily:'inherit',boxSizing:'border-box'}}
            onFocus={e=>e.target.style.borderColor=T.cyan}
            onBlur={e=>e.target.style.borderColor=T.b2}/>
      }
      {(hint||maxLen)&&(
        <div style={{display:'flex',justifyContent:'space-between',marginTop:4}}>
          {hint&&<span style={{fontSize:9,color:T.t4}}>{hint}</span>}
          {maxLen&&<span style={{fontSize:8,color:T.t4,fontFamily:'JetBrains Mono,monospace'}}>
            {(form[field]||'').length}/{maxLen}
          </span>}
        </div>
      )}
    </div>
  );

  return (
    <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',justifyContent:'flex-end'}}>
      {/* Backdrop */}
      <div onClick={onClose} style={{position:'absolute',inset:0,background:'rgba(2,8,16,.8)',backdropFilter:'blur(4px)'}}/>

      {/* Drawer */}
      <div style={{position:'relative',width:480,maxWidth:'100vw',background:T.surface,
        borderLeft:`1px solid ${T.b2}`,display:'flex',flexDirection:'column',
        boxShadow:'-20px 0 60px rgba(0,0,0,.8)',height:'100%',overflowY:'auto'}}>

        {/* Header */}
        <div style={{padding:'20px 24px',borderBottom:`1px solid ${T.b1}`,
          background:T.deep,flexShrink:0}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <div style={{fontFamily:'Syne,sans-serif',fontWeight:900,fontSize:18,color:T.t1}}>Edit Profile</div>
              <div style={{fontSize:9,color:T.t4,fontFamily:'JetBrains Mono,monospace',marginTop:2}}>
                IDENTITY · SOCIAL · RESEARCH · PRIVACY
              </div>
            </div>
            <button onClick={onClose} style={{background:'transparent',border:`1px solid ${T.b2}`,
              color:T.t3,padding:'6px 12px',borderRadius:3,cursor:'pointer',fontSize:10,fontFamily:'JetBrains Mono,monospace'}}>
              ✕ CLOSE
            </button>
          </div>

          {/* Tab nav */}
          <div style={{display:'flex',gap:4,marginTop:16}}>
            {tabs.map(tb=>(
              <button key={tb.id} onClick={()=>setTab(tb.id)} style={{
                padding:'6px 12px',border:`1px solid ${tab===tb.id?T.cyan:T.b1}`,
                background:tab===tb.id?`${T.cyan}14`:'transparent',
                color:tab===tb.id?T.cyan:T.t3,fontSize:9,cursor:'pointer',
                borderRadius:3,fontFamily:'JetBrains Mono,monospace',fontWeight:tab===tb.id?700:400,
              }}>{tb.label}</button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{flex:1,padding:'24px',overflowY:'auto'}}>
          {tab==='identity'&&<>
            <Input label="Display Name" field="display_name" placeholder="How you appear publicly" maxLen={60}
              hint="This replaces your real name on your public profile"/>
            <Input label="Bio" field="bio" placeholder="Who you are, what you build, what you research…" maxLen={300} type="textarea"
              hint="Be specific — this is your first impression"/>
            <Input label="Location" field="location" placeholder="City, Country or 'Remote'" maxLen={80}/>
            <Input label="Website / Portfolio" field="website" placeholder="https://yoursite.com" maxLen={120}/>
            <Input label="Affiliation" field="affiliation" placeholder="University, Company, or Independent Researcher" maxLen={100}/>
            <Input label="Pronouns" field="pronouns" placeholder="they/them, he/him, she/her…" maxLen={30}/>
            {/* Avatar URL */}
            <div style={{marginBottom:16}}>
              <label style={{display:'block',fontSize:9,color:T.t3,fontFamily:'JetBrains Mono,monospace',
                letterSpacing:'.1em',marginBottom:6,textTransform:'uppercase'}}>Avatar URL</label>
              <div style={{display:'flex',gap:10,alignItems:'center'}}>
                <Avatar src={form.avatar_url} name={form.display_name} size={48} tier={user.tier}/>
                <input value={form.avatar_url} onChange={e=>set('avatar_url')(e.target.value)}
                  placeholder="https://your-photo.com/avatar.jpg"
                  style={{flex:1,background:T.deep,border:`1px solid ${T.b2}`,color:T.t1,
                    padding:'10px 12px',fontSize:11,borderRadius:4,outline:'none',fontFamily:'inherit'}}
                  onFocus={e=>e.target.style.borderColor=T.cyan}
                  onBlur={e=>e.target.style.borderColor=T.b2}/>
              </div>
              <div style={{fontSize:9,color:T.t4,marginTop:4}}>Paste a direct image URL (Gravatar, GitHub avatar, etc)</div>
            </div>
          </>}

          {tab==='social'&&<>
            <div style={{padding:'12px 14px',background:T.raised,border:`1px solid ${T.b1}`,borderRadius:4,marginBottom:16}}>
              <div style={{fontSize:9,color:T.t3,lineHeight:1.7}}>
                Social links appear publicly on your profile only if you've chosen <strong style={{color:T.t1}}>not</strong> to be anonymous.
                They help the community find your other work.
              </div>
            </div>
            <Input label="GitHub" field="social_github" placeholder="username only (no @)" maxLen={80}/>
            <Input label="Twitter / X" field="social_twitter" placeholder="username only" maxLen={80}/>
            <Input label="LinkedIn" field="social_linkedin" placeholder="Full LinkedIn profile URL" maxLen={120}/>
            <Input label="Google Scholar" field="social_scholar" placeholder="Full Scholar profile URL" maxLen={200}/>
          </>}

          {tab==='focus'&&<>
            <div style={{fontSize:11,color:T.t2,lineHeight:1.7,marginBottom:16}}>
              Tag your research domains. These appear on your public profile and help others find you.
              <span style={{color:T.t4}}> Max 12.</span>
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
              {RESEARCH_TAGS.map(tag=>(
                <Chip key={tag} color={T.cyan} active={form.research_focus.includes(tag)}
                  onClick={()=>toggleTag(tag)}>{tag}</Chip>
              ))}
            </div>
            {form.research_focus.length>0&&(
              <div style={{marginTop:16,padding:'10px 12px',background:T.raised,
                border:`1px solid ${T.cyan}30`,borderRadius:4}}>
                <div style={{fontSize:9,color:T.cyan,fontFamily:'JetBrains Mono,monospace',marginBottom:6}}>
                  YOUR FOCUS ({form.research_focus.length}/12)
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {form.research_focus.map(t=><Chip key={t} color={T.cyan} active>{t}</Chip>)}
                </div>
              </div>
            )}
          </>}

          {tab==='privacy'&&<>
            {/* Anonymity toggle */}
            <div style={{padding:'16px',background:T.raised,border:`1px solid ${T.b1}`,borderRadius:4,marginBottom:20}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
                <div>
                  <div style={{fontFamily:'Syne,sans-serif',fontWeight:700,fontSize:14,color:T.t1,marginBottom:4}}>
                    Anonymous Mode
                  </div>
                  <div style={{fontSize:11,color:T.t3,lineHeight:1.65,maxWidth:300}}>
                    When on: your real name, location, affiliation, and social links are hidden from public profile.
                    Your work can still be published — just attributed to "Anonymous Researcher."
                    XP, badges, and rank are still tracked privately.
                  </div>
                </div>
                <button onClick={()=>set('is_anonymous')(!form.is_anonymous)}
                  style={{
                    width:48,height:26,borderRadius:13,
                    background:form.is_anonymous?T.violet:T.b1,
                    border:'none',cursor:'pointer',position:'relative',flexShrink:0,
                    transition:'background .2s',
                  }}>
                  <div style={{
                    position:'absolute',top:3,
                    left:form.is_anonymous?24:3,
                    width:20,height:20,borderRadius:'50%',
                    background:'#fff',transition:'left .2s',
                    boxShadow:'0 1px 4px rgba(0,0,0,.5)',
                  }}/>
                </button>
              </div>
              {form.is_anonymous&&(
                <div style={{marginTop:10,padding:'8px 10px',background:`${T.violet}12`,
                  border:`1px solid ${T.violet}44`,borderRadius:3,fontSize:10,color:T.violet,lineHeight:1.6}}>
                  🕶 Ghost Protocol active — your identity is shielded. Your research speaks for itself.
                </div>
              )}
            </div>

            {/* Cover style */}
            <div>
              <div style={{fontSize:9,color:T.t3,fontFamily:'JetBrains Mono,monospace',
                letterSpacing:'.1em',marginBottom:10,textTransform:'uppercase'}}>Profile Cover</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                {COVER_STYLES.map(cs=>(
                  <div key={cs.id} onClick={()=>set('cover_style')(cs.id)}
                    style={{
                      height:56,borderRadius:4,cursor:'pointer',
                      background:cs.css,
                      border:`2px solid ${form.cover_style===cs.id?T.cyan:T.b1}`,
                      display:'flex',alignItems:'flex-end',padding:'0 8px 6px',
                      transition:'border-color .12s',
                    }}>
                    <span style={{fontSize:8,color:form.cover_style===cs.id?T.cyan:T.t4,
                      fontFamily:'JetBrains Mono,monospace',fontWeight:form.cover_style===cs.id?700:400}}>
                      {cs.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>}
        </div>

        {/* Footer */}
        <div style={{padding:'16px 24px',borderTop:`1px solid ${T.b1}`,
          background:T.deep,flexShrink:0,display:'flex',gap:10,alignItems:'center'}}>
          {error&&<span style={{flex:1,fontSize:10,color:T.red}}>{error}</span>}
          {saved&&<span style={{flex:1,fontSize:10,color:T.green,fontFamily:'JetBrains Mono,monospace'}}>✓ Saved</span>}
          {!error&&!saved&&<span style={{flex:1}}/>}
          <button onClick={onClose} style={{padding:'8px 16px',background:'transparent',
            border:`1px solid ${T.b2}`,color:T.t3,fontSize:10,cursor:'pointer',borderRadius:3,
            fontFamily:'JetBrains Mono,monospace'}}>Cancel</button>
          <button onClick={save} disabled={saving} style={{
            padding:'8px 20px',background:saving?T.b1:`${T.cyan}22`,
            border:`1px solid ${saving?T.b2:T.cyan}`,color:saving?T.t4:T.cyan,
            fontSize:10,cursor:saving?'wait':'pointer',borderRadius:3,
            fontFamily:'JetBrains Mono,monospace',fontWeight:700,
          }}>{saving?'Saving…':'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PROFILE PAGE ───────────────────────────────────────────────────────

export default function ProfilePage({ username: propUsername, onClose }) {
  const { user: authUser, updateUser, logout } = useAuth();
  const username = propUsername || authUser?.username;
  const isOwnProfile = authUser && username === authUser.username;

  const [profile,    setProfile]    = useState(null);
  const [timeline,   setTimeline]   = useState([]);
  const [work,       setWork]       = useState([]);
  const [stats,      setStats]      = useState(null);
  const [myExps,     setMyExps]     = useState([]);
  const [likedIds,   setLikedIds]   = useState(new Set());
  const [loading,    setLoading]    = useState(true);
  const [tab,        setTab]        = useState('overview');
  const [editOpen,   setEditOpen]   = useState(false);
  const [error,      setError]      = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePw,   setDeletePw]   = useState('');
  const [deleteErr,  setDeleteErr]  = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Load profile
  useEffect(() => {
    if (!username) return;
    setLoading(true);
    apiGet(`/users/${username}`)
      .then(d => { setProfile(d.user); setTimeline(d.timeline||[]); setWork(d.featured_work||[]); })
      .catch(e => setError(e.message || 'Profile not found'))
      .finally(() => setLoading(false));
  }, [username]);

  // Load own stats + experiments
  useEffect(() => {
    if (!isOwnProfile || !authUser) return;
    apiGet('/users/me/stats').then(setStats).catch(()=>{});
    apiGet('/experiments').then(d=>setMyExps(d.experiments||[])).catch(()=>{});
  }, [isOwnProfile, authUser]);

  const handleLike = useCallback(async (eid) => {
    try {
      const {liked, likes} = await apiPost(`/experiments/${eid}/like`, {});
      const update = arr => arr.map(e => e.id===eid ? {...e, likes} : e);
      setWork(update); setMyExps(update);
      setLikedIds(prev => { const n=new Set(prev); liked?n.add(eid):n.delete(eid); return n; });
    } catch {}
  }, []);

  const handleTogglePublic = useCallback(async (exp) => {
    try {
      await apiPatch(`/experiments/${exp.id}`, {is_public: !exp.is_public});
      setMyExps(arr => arr.map(e => e.id===exp.id ? {...e, is_public: !e.is_public} : e));
    } catch {}
  }, []);

  const handleEditSaved = useCallback((updated) => {
    setProfile(updated);
    updateUser?.(updated);
  }, [updateUser]);

  const handleDeleteAccount = useCallback(async () => {
    if (!deletePw) { setDeleteErr('Enter your password to confirm.'); return; }
    setDeleteBusy(true);
    setDeleteErr('');
    try {
      await authApi.deleteAccount(deletePw);
      authApi.logout();
      logout?.();
      window.location.href = '/';
    } catch (ex) {
      setDeleteErr(ex?.data?.error || ex?.message || 'Deletion failed. Check your password.');
    } finally {
      setDeleteBusy(false);
    }
  }, [deletePw, logout]);

  if (loading) return (
    <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:12,minHeight:400}}>
      <div style={{fontSize:9,color:T.t4,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.2em'}}>LOADING PROFILE…</div>
      <div style={{width:200,height:2,background:T.b1,borderRadius:2,overflow:'hidden'}}>
        <div style={{width:'60%',height:'100%',background:T.cyan,borderRadius:2,animation:'pulse 1.5s ease-in-out infinite'}}/>
      </div>
    </div>
  );

  if (error || !profile) return (
    <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:12}}>
      <div style={{fontSize:14,color:T.t2}}>{error || 'Profile not found'}</div>
      {onClose&&<button onClick={onClose} style={{padding:'6px 14px',background:'transparent',border:`1px solid ${T.b2}`,color:T.t3,cursor:'pointer',borderRadius:3,fontSize:10}}>← Back</button>}
    </div>
  );

  const level    = getLevel(profile.total_xp||0);
  const nextLvl  = getNextLevel(profile.total_xp||0);
  const pct      = xpPct(profile.total_xp||0);
  const cover    = COVER_STYLES.find(c=>c.id===profile.cover_style)||COVER_STYLES[0];
  const tierCfg  = TIER[profile.tier]||TIER.free;
  const displayName = profile.is_anonymous ? 'Anonymous Researcher' : (profile.display_name||profile.full_name||profile.username);

  const TABS = [
    {id:'overview', label:'Overview'},
    {id:'work',     label:`Work (${profile.public_experiments_count||0})`},
    {id:'badges',   label:`Badges (${profile.badges?.length||0})`},
    {id:'activity', label:'Activity'},
    ...(isOwnProfile ? [{id:'private', label:'My Labs'}, {id:'settings', label:'Settings'}] : []),
  ];

  return (
    <div style={{flex:1,overflowY:'auto',background:T.bg}}>
      {editOpen&&(
        <EditDrawer user={profile} onClose={()=>setEditOpen(false)} onSaved={handleEditSaved}/>
      )}

      {/* ── COVER + HERO ── */}
      <div style={{position:'relative'}}>
        {/* Cover band */}
        <div style={{height:200,background:cover.css,position:'relative',overflow:'hidden'}}>
          {/* Subtle grid overlay */}
          <div style={{position:'absolute',inset:0,backgroundImage:'radial-gradient(circle, rgba(34,211,238,.04) 1px, transparent 1px)',backgroundSize:'32px 32px'}}/>
          {/* Fade to surface at bottom */}
          <div style={{position:'absolute',bottom:0,left:0,right:0,height:80,background:`linear-gradient(transparent,${T.bg})`}}/>
          {/* Top-right actions */}
          <div style={{position:'absolute',top:16,right:16,display:'flex',gap:8}}>
            {onClose&&(
              <button onClick={onClose} style={{padding:'6px 14px',background:'rgba(2,8,16,.7)',
                border:`1px solid ${T.b2}`,color:T.t2,fontSize:9,cursor:'pointer',
                borderRadius:3,backdropFilter:'blur(8px)',fontFamily:'JetBrains Mono,monospace'}}>
                ← BACK
              </button>
            )}
            {isOwnProfile&&(
              <button onClick={()=>setEditOpen(true)} style={{padding:'6px 14px',
                background:`${T.cyan}20`,border:`1px solid ${T.cyan}`,color:T.cyan,
                fontSize:9,cursor:'pointer',borderRadius:3,backdropFilter:'blur(8px)',
                fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>
                ✎ EDIT PROFILE
              </button>
            )}
          </div>
        </div>

        {/* Avatar row — overlaps cover */}
        <div style={{padding:'0 28px',marginTop:-56,position:'relative',zIndex:10}}>
          <div style={{display:'flex',gap:20,alignItems:'flex-end',flexWrap:'wrap'}}>
            <Avatar src={profile.is_anonymous?'':profile.avatar_url} name={displayName} size={104} tier={profile.tier}/>
            <div style={{paddingBottom:8,flex:1,minWidth:200}}>
              <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:4}}>
                <h1 style={{fontFamily:'Syne,sans-serif',fontWeight:900,fontSize:22,color:T.t1,margin:0,letterSpacing:'-.02em'}}>
                  {displayName}
                </h1>
                <TierPill tier={profile.tier} size="lg"/>
                {profile.is_anonymous&&(
                  <span style={{fontSize:8,padding:'2px 8px',background:`${T.violet}18`,
                    border:`1px solid ${T.violet}44`,borderRadius:2,color:T.violet,
                    fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>
                    🕶 ANON
                  </span>
                )}
              </div>
              <div style={{fontSize:11,color:T.t3,fontFamily:'JetBrains Mono,monospace',marginBottom:6}}>
                @{profile.username}
                {profile.pronouns&&<span style={{marginLeft:10,color:T.t4}}>· {profile.pronouns}</span>}
              </div>
              {/* Meta row */}
              <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
                {profile.affiliation&&!profile.is_anonymous&&(
                  <span style={{fontSize:10,color:T.t3}}>🏛 {profile.affiliation}</span>
                )}
                {profile.location&&!profile.is_anonymous&&(
                  <span style={{fontSize:10,color:T.t3}}>📍 {profile.location}</span>
                )}
                {profile.website&&!profile.is_anonymous&&(
                  <a href={profile.website} target="_blank" rel="noreferrer"
                    style={{fontSize:10,color:T.cyan,textDecoration:'none'}}>
                    🔗 {profile.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
                <span style={{fontSize:10,color:T.t4}}>Joined {fmtDate(profile.created_at)}</span>
                {profile.profile_views>0&&(
                  <span style={{fontSize:10,color:T.t4}}>◎ {fmt(profile.profile_views)} views</span>
                )}
              </div>
            </div>

            {/* Social icons — right side */}
            {!profile.is_anonymous&&(
              <div style={{display:'flex',gap:8,paddingBottom:8,flexShrink:0}}>
                {profile.social_github&&(
                  <a href={`https://github.com/${profile.social_github}`} target="_blank" rel="noreferrer"
                    style={{padding:'6px 10px',background:T.raised,border:`1px solid ${T.b1}`,borderRadius:3,
                      color:T.t2,fontSize:9,textDecoration:'none',fontFamily:'JetBrains Mono,monospace',
                      transition:'border-color .12s'}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=T.b2}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=T.b1}>
                    GH
                  </a>
                )}
                {profile.social_twitter&&(
                  <a href={`https://twitter.com/${profile.social_twitter}`} target="_blank" rel="noreferrer"
                    style={{padding:'6px 10px',background:T.raised,border:`1px solid ${T.b1}`,borderRadius:3,
                      color:T.t2,fontSize:9,textDecoration:'none',fontFamily:'JetBrains Mono,monospace'}}>
                    X
                  </a>
                )}
                {profile.social_linkedin&&(
                  <a href={profile.social_linkedin} target="_blank" rel="noreferrer"
                    style={{padding:'6px 10px',background:T.raised,border:`1px solid ${T.b1}`,borderRadius:3,
                      color:T.t2,fontSize:9,textDecoration:'none',fontFamily:'JetBrains Mono,monospace'}}>
                    IN
                  </a>
                )}
                {profile.social_scholar&&(
                  <a href={profile.social_scholar} target="_blank" rel="noreferrer"
                    style={{padding:'6px 10px',background:T.raised,border:`1px solid ${T.b1}`,borderRadius:3,
                      color:T.t2,fontSize:9,textDecoration:'none',fontFamily:'JetBrains Mono,monospace'}}>
                    GS
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── BIO + LEVEL BAR ── */}
      <div style={{padding:'16px 28px 0'}}>
        {profile.bio&&(
          <p style={{fontSize:13,color:T.t2,lineHeight:1.75,margin:'0 0 14px',maxWidth:640}}>{profile.bio}</p>
        )}

        {/* Research focus tags */}
        {profile.research_focus?.length>0&&(
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:14}}>
            {profile.research_focus.map(tag=><Chip key={tag} color={T.cyan}>{tag}</Chip>)}
          </div>
        )}

        {/* XP + Level bar */}
        <div style={{background:T.surface,border:`1px solid ${T.b1}`,borderRadius:6,
          padding:'14px 18px',marginBottom:4,
          borderLeft:`3px solid ${tierCfg.color}`}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8,flexWrap:'wrap',gap:8}}>
            <div style={{display:'flex',alignItems:'baseline',gap:8}}>
              <span style={{fontFamily:'Syne,sans-serif',fontWeight:900,fontSize:18,color:tierCfg.color}}>{level.label}</span>
              <span style={{fontSize:10,color:T.t4,fontFamily:'JetBrains Mono,monospace'}}>LVL {level.num}</span>
            </div>
            <div style={{display:'flex',gap:20,alignItems:'center'}}>
              <span style={{fontSize:11,color:T.t2,fontFamily:'JetBrains Mono,monospace'}}>
                <span style={{color:T.gold,fontWeight:700}}>{fmt(profile.total_xp||0)}</span> XP
              </span>
              {stats?.rank&&(
                <span style={{fontSize:11,color:T.t2,fontFamily:'JetBrains Mono,monospace'}}>
                  Rank <span style={{color:T.cyan,fontWeight:700}}>#{stats.rank}</span>
                  <span style={{color:T.t4}}> / {fmt(stats.total_users)}</span>
                </span>
              )}
            </div>
          </div>
          <PBar pct={pct} color={tierCfg.color} h={6} glow/>
          <div style={{display:'flex',justifyContent:'space-between',marginTop:4}}>
            <span style={{fontSize:8,color:T.t4,fontFamily:'JetBrains Mono,monospace'}}>{pct}% to next level</span>
            <span style={{fontSize:8,color:T.t4,fontFamily:'JetBrains Mono,monospace'}}>Next: {nextLvl.label}</span>
          </div>
        </div>
      </div>

      {/* ── STAT STRIP ── */}
      <div style={{padding:'0 28px',marginTop:4}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',background:T.surface,
          border:`1px solid ${T.b1}`,borderRadius:6,overflow:'hidden'}}>
          {[
            [profile.total_xp||0,       'XP',           T.gold,   null],
            [profile.streak||0,         'STREAK',       T.orange, 'days'],
            [profile.labs_completed||0, 'LABS DONE',    T.green,  null],
            [profile.experiments_count||0,'EXPERIMENTS', T.cyan,   null],
            [profile.public_experiments_count||0,'PUBLIC', T.violet, null],
            [profile.total_likes_received||0,'LIKES GOT', T.pink,  null],
          ].map(([val,label,color,sub],i)=>(
            <div key={i} style={{borderRight:i<5?`1px solid ${T.b0}`:'none'}}>
              <StatNum value={val} label={label} color={color} sub={sub}/>
            </div>
          ))}
        </div>
      </div>

      {/* ── TAB NAV ── */}
      <div style={{display:'flex',gap:0,margin:'16px 28px 0',borderBottom:`1px solid ${T.b1}`}}>
        {TABS.map(tb=>(
          <button key={tb.id} onClick={()=>setTab(tb.id)} style={{
            padding:'10px 18px',border:'none',borderBottom:`2px solid ${tab===tb.id?T.cyan:'transparent'}`,
            background:'transparent',color:tab===tb.id?T.cyan:T.t3,
            fontSize:10,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',
            fontWeight:tab===tb.id?700:400,transition:'color .12s, border-color .12s',
            letterSpacing:'.06em',
          }}>{tb.label}</button>
        ))}
      </div>

      {/* ── TAB CONTENT ── */}
      <div style={{padding:'20px 28px 40px'}}>

        {/* OVERVIEW */}
        {tab==='overview'&&(
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
            {/* Left col */}
            <div style={{display:'flex',flexDirection:'column',gap:16}}>
              {/* Activity heatmap */}
              {(isOwnProfile&&stats?.activity_heatmap)&&(
                <div style={{background:T.surface,border:`1px solid ${T.b1}`,borderRadius:6,padding:'16px 18px'}}>
                  <div style={{fontSize:9,color:T.cyan,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.12em',fontWeight:700,marginBottom:12}}>
                    ACTIVITY HEATMAP — 90 DAYS
                  </div>
                  <Heatmap activity={stats.activity_heatmap}/>
                </div>
              )}

              {/* Recent badges */}
              <div style={{background:T.surface,border:`1px solid ${T.b1}`,borderRadius:6,padding:'16px 18px'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
                  <div style={{fontSize:9,color:T.gold,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.12em',fontWeight:700}}>
                    BADGES EARNED ({profile.badges?.length||0}/{BADGES.length})
                  </div>
                  <button onClick={()=>setTab('badges')} style={{fontSize:8,color:T.t4,background:'transparent',border:'none',cursor:'pointer',fontFamily:'JetBrains Mono,monospace'}}>VIEW ALL →</button>
                </div>
                <BadgeGrid earned={profile.badges||[]} mini/>
              </div>

              {/* Stats breakdown — own only */}
              {isOwnProfile&&stats?.module_usage&&Object.keys(stats.module_usage).length>0&&(
                <div style={{background:T.surface,border:`1px solid ${T.b1}`,borderRadius:6,padding:'16px 18px'}}>
                  <div style={{fontSize:9,color:T.violet,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.12em',fontWeight:700,marginBottom:12}}>
                    MODULE USAGE
                  </div>
                  {Object.entries(stats.module_usage).sort((a,b)=>b[1]-a[1]).slice(0,8).map(([mod,cnt])=>{
                    const max = Math.max(...Object.values(stats.module_usage));
                    return (
                      <div key={mod} style={{marginBottom:8}}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                          <span style={{fontSize:10,color:T.t2}}>{mod}</span>
                          <span style={{fontSize:9,color:T.t3,fontFamily:'JetBrains Mono,monospace'}}>{cnt}</span>
                        </div>
                        <PBar pct={cnt/max*100} color={T.violet} h={4}/>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right col */}
            <div style={{display:'flex',flexDirection:'column',gap:16}}>
              {/* Featured work preview */}
              {work.length>0&&(
                <div style={{background:T.surface,border:`1px solid ${T.b1}`,borderRadius:6,padding:'16px 18px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
                    <div style={{fontSize:9,color:T.green,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.12em',fontWeight:700}}>
                      FEATURED WORK
                    </div>
                    <button onClick={()=>setTab('work')} style={{fontSize:8,color:T.t4,background:'transparent',border:'none',cursor:'pointer',fontFamily:'JetBrains Mono,monospace'}}>ALL →</button>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:10}}>
                    {work.slice(0,3).map(exp=>(
                      <div key={exp.id} style={{display:'flex',gap:10,alignItems:'center',
                        padding:'8px 10px',background:T.raised,border:`1px solid ${T.b0}`,borderRadius:4}}>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,color:T.t1,fontFamily:'Syne,sans-serif',fontWeight:700,marginBottom:2}}>{exp.title||'Untitled'}</div>
                          <div style={{fontSize:9,color:T.t4,fontFamily:'JetBrains Mono,monospace'}}>{exp.module||'—'} · ♥ {exp.likes||0} · ◎ {exp.views||0}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent activity */}
              <div style={{background:T.surface,border:`1px solid ${T.b1}`,borderRadius:6,padding:'16px 18px'}}>
                <div style={{fontSize:9,color:T.orange,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.12em',fontWeight:700,marginBottom:12}}>
                  RECENT ACTIVITY
                </div>
                <Timeline items={timeline.slice(0,6)}/>
                {timeline.length>6&&(
                  <button onClick={()=>setTab('activity')} style={{marginTop:8,padding:'5px 12px',background:'transparent',
                    border:`1px solid ${T.b1}`,color:T.t4,fontSize:8,cursor:'pointer',borderRadius:3,
                    fontFamily:'JetBrains Mono,monospace'}}>
                    SEE ALL →
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* WORK */}
        {tab==='work'&&(
          <div>
            {work.length===0
              ? <div style={{textAlign:'center',padding:'60px 0',color:T.t4,fontFamily:'JetBrains Mono,monospace',fontSize:11}}>
                  No public work yet.
                  {isOwnProfile&&<div style={{marginTop:8,fontSize:10}}>Make some experiments public to build your portfolio.</div>}
                </div>
              : <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
                  {work.map(exp=>(
                    <WorkCard key={exp.id} exp={exp} isOwn={false}
                      onLike={handleLike} liked={likedIds.has(exp.id)}/>
                  ))}
                </div>
            }
          </div>
        )}

        {/* BADGES */}
        {tab==='badges'&&(
          <div>
            <div style={{marginBottom:16,padding:'12px 14px',background:T.raised,border:`1px solid ${T.b1}`,borderRadius:4}}>
              <div style={{fontSize:11,color:T.t2,lineHeight:1.7}}>
                <span style={{color:T.gold,fontWeight:700}}>{profile.badges?.length||0}</span> of {BADGES.length} badges earned.
                {' '}Badges are permanent proof of achievement — blockchain-verifiable, impossible to fake.
              </div>
            </div>
            <BadgeGrid earned={profile.badges||[]}/>
          </div>
        )}

        {/* ACTIVITY */}
        {tab==='activity'&&(
          <div>
            {isOwnProfile&&stats?.activity_heatmap&&(
              <div style={{background:T.surface,border:`1px solid ${T.b1}`,borderRadius:6,
                padding:'16px 18px',marginBottom:20}}>
                <div style={{fontSize:9,color:T.cyan,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.12em',fontWeight:700,marginBottom:12}}>
                  ACTIVITY HEATMAP
                </div>
                <Heatmap activity={stats.activity_heatmap}/>
              </div>
            )}
            <Timeline items={timeline}/>
          </div>
        )}

        {/* PRIVATE — own experiments manager */}
        {tab==='private'&&isOwnProfile&&(
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <div style={{fontSize:11,color:T.t3}}>
                <span style={{color:T.t1,fontWeight:700}}>{myExps.length}</span> saved experiments ·{' '}
                <span style={{color:T.green}}>{myExps.filter(e=>e.is_public).length} public</span> ·{' '}
                <span style={{color:T.t4}}>{myExps.filter(e=>!e.is_public).length} private</span>
              </div>
            </div>
            {myExps.length===0
              ? <div style={{textAlign:'center',padding:'60px 0',color:T.t4,fontFamily:'JetBrains Mono,monospace'}}>No experiments saved yet. Start a lab to begin.</div>
              : <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
                  {myExps.map(exp=>(
                    <WorkCard key={exp.id} exp={exp} isOwn={true}
                      onLike={handleLike} liked={likedIds.has(exp.id)}
                      onTogglePublic={handleTogglePublic}/>
                  ))}
                </div>
            }
          </div>
        )}

        {/* SETTINGS (own profile only) */}
        {tab==='settings'&&isOwnProfile&&(
          <div style={{maxWidth:520}}>
            <div style={{background:T.surface,border:`1px solid ${T.red}44`,borderRadius:6,padding:'20px 22px'}}>
              <div style={{fontSize:9,color:T.red,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.12em',fontWeight:700,marginBottom:12}}>DANGER ZONE</div>
              <div style={{fontSize:12,color:T.t2,marginBottom:6,fontWeight:600}}>Delete Account</div>
              <div style={{fontSize:11,color:T.t3,lineHeight:1.7,marginBottom:16}}>
                Permanently deletes your account and all associated data including published experiments and workspaces.
                This action is immediate, irreversible, and cannot be undone.
              </div>
              {!deleteOpen ? (
                <button
                  onClick={()=>{ setDeleteOpen(true); setDeleteErr(''); setDeletePw(''); }}
                  style={{ padding:'8px 18px',background:`${T.red}12`,border:`1px solid ${T.red}55`,
                    color:T.red,fontSize:10,fontFamily:'JetBrains Mono,monospace',fontWeight:700,
                    letterSpacing:'.08em',borderRadius:3,cursor:'pointer' }}
                >
                  DELETE MY ACCOUNT
                </button>
              ) : (
                <div style={{background:`${T.red}08`,border:`1px solid ${T.red}33`,borderRadius:4,padding:'16px 18px'}}>
                  <div style={{fontSize:11,color:T.t2,marginBottom:12,lineHeight:1.6}}>
                    Enter your current password to confirm account deletion:
                  </div>
                  {deleteErr && (
                    <div style={{fontSize:10,color:T.red,marginBottom:10,padding:'7px 10px',background:`${T.red}10`,border:`1px solid ${T.red}33`,borderRadius:3}}>
                      {deleteErr}
                    </div>
                  )}
                  <input
                    type="password"
                    value={deletePw}
                    onChange={e=>setDeletePw(e.target.value)}
                    placeholder="Your current password"
                    autoFocus
                    style={{ width:'100%',padding:'9px 12px',background:T.deep,border:`1px solid ${T.b2}`,
                      color:T.t1,fontSize:12,fontFamily:'JetBrains Mono,monospace',borderRadius:3,
                      outline:'none',boxSizing:'border-box',marginBottom:12 }}
                  />
                  <div style={{display:'flex',gap:10}}>
                    <button onClick={handleDeleteAccount} disabled={deleteBusy}
                      style={{ padding:'8px 18px',background:deleteBusy?T.b1:`${T.red}22`,
                        border:`1px solid ${deleteBusy?T.b2:T.red}`,color:deleteBusy?T.t4:T.red,
                        fontSize:10,fontFamily:'JetBrains Mono,monospace',fontWeight:700,
                        letterSpacing:'.08em',borderRadius:3,cursor:deleteBusy?'wait':'pointer' }}
                    >
                      {deleteBusy ? 'DELETING...' : 'CONFIRM DELETE'}
                    </button>
                    <button onClick={()=>{ setDeleteOpen(false); setDeleteErr(''); setDeletePw(''); }}
                      style={{ padding:'8px 16px',background:'transparent',border:`1px solid ${T.b2}`,
                        color:T.t3,fontSize:10,fontFamily:'JetBrains Mono,monospace',
                        borderRadius:3,cursor:'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
